import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {type AxiosResponse, isAxiosError} from "axios"

import api, {klaraDashboardKey, queryKey} from "../services"
import requestError from "../services/requestError.tsx"
import {
  AKlaraCompanySettings,
  AKlaraDashboardDealChipValues,
  AKlaraDashboardDealsMeta,
  AKlaraDashboardDealSortByValues,
  AKlaraDashboardDealViewValues,
  AKlaraDashboardGranularityValues,
  AKlaraDashboardUpdateCenterMeta,
  ASalesAssistantDealPriorities,
  ASalesAssistantDealsListData,
  ASalesAssistantDealsListParamsDashboardEnum,
  ASalesAssistantDealSortDirectionValues,
  ASalesAssistantUpdateCenterListData,
  ASalesAssistantUpdateCenterListParamsDashboardEnum,
} from "../services/types.generated.ts"
import {useInfinitePaginatedQuery} from "./general.ts"

/** Shared by every consumer of the dashboard update feed so they read the same cached pages. */
export const KLARA_UPDATE_CENTER_PAGE_SIZE = 20

export type TKlaraDashboardFeedList = Extract<
  ASalesAssistantUpdateCenterListData,
  {meta: AKlaraDashboardUpdateCenterMeta}
>

/** The feed answers in its dashboard shape only when asked with dashboard=true; the event total proves it did. */
export const isDashboardFeedList = (data: ASalesAssistantUpdateCenterListData): data is TKlaraDashboardFeedList =>
  data.meta != null && "total_event_count" in data.meta

/**
 * The dashboard update feed of every entry type, bounded by `from` / `to` (lifetime sends neither, `to` alone means
 * "up to"); with a deal it lists only that deal's sessions, pruned to its events.
 */
export const useKlaraUpdateCenterQuery = ({
  search = "",
  salesPersonExternalId,
  dealId,
  from,
  to,
}: {
  search?: string
  salesPersonExternalId?: string
  dealId?: number
  from?: string
  to?: string
}) => {
  return useInfinitePaginatedQuery(
    queryKey.klaraUpdateCenter({search, salesPersonExternalId, dealId, from, to}),
    async (paginationParams): Promise<AxiosResponse<TKlaraDashboardFeedList>> => {
      const response = await api.saasCompanyUsers.salesAssistantUpdateCenterList({
        dashboard: ASalesAssistantUpdateCenterListParamsDashboardEnum.True,
        search: search || undefined,
        sales_person_external_id: salesPersonExternalId,
        deal_id: dealId,
        from,
        to,
        ...paginationParams,
      })
      if (!isDashboardFeedList(response.data)) {
        throw new Error("The update feed did not answer in dashboard mode")
      }
      return {...response, data: response.data}
    },
    // A scope or search change keeps the previous sessions on screen (dimmed) until the new feed arrives.
    {pageSize: KLARA_UPDATE_CENTER_PAGE_SIZE, placeholderData: keepPreviousData}
  )
}

const DEAL_DETAIL_MAX_RETRIES = 3

/** One lead with its flows, stage, flags and activities; outside the company's deal universe it is a 404. */
export const useKlaraDealDetailQuery = (dealId: number) => {
  return useQuery({
    queryKey: queryKey.klaraDealDetail(dealId),
    queryFn: () => api.saasCompanyUsers.salesAssistantDashboardDealsDetail(dealId),
    // A 404 is a final answer (the lead is outside the dashboard's reach), so only other failures are retried.
    retry: (failureCount, error) =>
      !(isAxiosError(error) && error.response?.status === 404) && failureCount < DEAL_DETAIL_MAX_RETRIES,
  })
}

export type TKlaraDashboardDealsList = Extract<ASalesAssistantDealsListData, {meta: AKlaraDashboardDealsMeta}>

/** The deals endpoint answers in its dashboard shape only when asked with dashboard=true; the markers prove it did. */
export const isDashboardDealsList = (data: ASalesAssistantDealsListData): data is TKlaraDashboardDealsList =>
  data.meta != null && "mode" in data.meta && "counts" in data.meta

export type TKlaraDealsFilter = {
  from?: string
  to?: string
  view: AKlaraDashboardDealViewValues
  /** Replaces the view when set. */
  chip?: AKlaraDashboardDealChipValues
  priorities?: ASalesAssistantDealPriorities[]
  salesPersonExternalId?: string
  search?: string
  sortBy?: AKlaraDashboardDealSortByValues
  sortDirection?: ASalesAssistantDealSortDirectionValues
  page: number
  pageSize: number
}

/**
 * The leads table: dashboard-mode deals of the period with their facet counts; lifetime sends neither bound.
 * Pagination is controlled by the caller (the dashboard filters), so a filter, period or person change resets the
 * page before the request is made.
 */
export const useKlaraDealsQuery = ({
  from,
  to,
  view,
  chip,
  priorities = [],
  salesPersonExternalId,
  search = "",
  sortBy,
  sortDirection,
  page,
  pageSize,
}: TKlaraDealsFilter) => {
  return useQuery({
    queryKey: queryKey.klaraDeals({
      from,
      to,
      view,
      chip,
      priorities,
      salesPersonExternalId,
      search,
      sortBy,
      sortDirection,
      page,
      pageSize,
    }),
    queryFn: async () => {
      const response = await api.saasCompanyUsers.salesAssistantDealsList({
        dashboard: ASalesAssistantDealsListParamsDashboardEnum.True,
        from,
        to,
        ...(chip ? {chip} : {view}),
        "priorities[]": priorities.length > 0 ? priorities : undefined,
        sales_person_external_id: salesPersonExternalId,
        search: search || undefined,
        sort_by: sortBy,
        sort_direction: sortBy ? sortDirection : undefined,
        page,
        per_page: pageSize,
      })
      if (!isDashboardDealsList(response.data)) {
        throw new Error("The deals endpoint did not answer in dashboard mode")
      }
      return {...response, data: response.data}
    },
    placeholderData: keepPreviousData,
  })
}

/** Team-wide KPI figures for the period; lifetime sends neither bound. */
export const useKlaraSummaryQuery = ({from, to}: {from?: string; to?: string}) => {
  return useQuery({
    queryKey: queryKey.klaraSummary({from, to}),
    queryFn: () => api.saasCompanyUsers.salesAssistantDashboardSummaryList({from, to}),
    // A period change keeps the previous figures on screen (dimmed) until the new ones arrive.
    placeholderData: keepPreviousData,
  })
}

/** Team-wide Klara updates per bucket of the period; lifetime sends neither bound. */
export const useKlaraUpdatesOverTimeQuery = ({
  from,
  to,
  granularity,
}: {
  from?: string
  to?: string
  granularity: AKlaraDashboardGranularityValues
}) => {
  return useQuery({
    queryKey: queryKey.klaraUpdatesOverTime({from, to, granularity}),
    queryFn: () => api.saasCompanyUsers.salesAssistantDashboardUpdatesOverTimeList({from, to, granularity}),
    // A period change keeps the previous buckets on screen (dimmed) until the new ones arrive.
    placeholderData: keepPreviousData,
  })
}

/** Per-person figures for the period, in the backend's ranking; lifetime sends neither bound. */
export const useKlaraSalesPeopleQuery = ({from, to}: {from?: string; to?: string}) => {
  return useQuery({
    queryKey: queryKey.klaraSalesPeople({from, to}),
    queryFn: () => api.saasCompanyUsers.salesAssistantDashboardSalesPeopleList({from, to}),
    // A period change keeps the previous rows on screen (dimmed) until the new ones arrive.
    placeholderData: keepPreviousData,
  })
}

/**
 * Sets a lead's priority by hand; every dashboard figure may depend on it, so the whole dashboard refetches.
 * A failure is reported even when the row that started it is gone by then.
 */
export const useKlaraUpdateDealPriorityMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({dealId, priority}: {dealId: number; priority: ASalesAssistantDealPriorities}) =>
      api.saasCompanyUsers.salesAssistantDashboardDealsPartialUpdate(dealId, {deal: {priority}}),
    onSuccess: () => queryClient.invalidateQueries({queryKey: klaraDashboardKey}),
    onError: requestError,
  })
}

/**
 * Notifies the lead's owner about the lead; changes no dashboard data. The outcome callbacks are set on the hook, not
 * per call, so they still run when the row that started the reminder is gone by then.
 */
export const useKlaraRemindMutation = ({
  onSuccess,
  onError,
}: {onSuccess?: () => void; onError?: (error: unknown) => void} = {}) => {
  return useMutation({
    mutationFn: (dealId: number) => api.saasCompanyUsers.salesAssistantDashboardDealsRemindCreate(dealId),
    onSuccess,
    onError,
  })
}

/** The company's Klara settings, such as the lead coverage target. */
export const useKlaraCompanySettingsQuery = () => {
  return useQuery({
    queryKey: queryKey.klaraCompanySettings,
    queryFn: () => api.saasCompanyUsers.salesAssistantCompanySettingsList(),
  })
}

/**
 * Saves the company's Klara settings and keeps the saved values in the cache. The summary of every period carries the
 * coverage target, so all of them refetch.
 */
export const useKlaraCompanySettingsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (companySettings: AKlaraCompanySettings) =>
      api.saasCompanyUsers.salesAssistantCompanySettingsPartialUpdate({company_settings: companySettings}),
    onSuccess: async response => {
      // A load that started before the save would otherwise land after it and put the old values back.
      await queryClient.cancelQueries({queryKey: queryKey.klaraCompanySettings, exact: true})
      queryClient.setQueryData(queryKey.klaraCompanySettings, response)
      void queryClient.invalidateQueries({queryKey: queryKey.klaraSummaries})
    },
  })
}
